import { ChangeDetectionStrategy, Component } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { from } from 'rxjs';
import {
  PrimaryTrade,
  PrimaryTradeLicenseRequirement,
  SubNationalDivision
} from 'src/app/classes/models';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { AddEditTradeRequirementModalComponent } from 'src/app/modals/add-edit-trade-requirement-modal/add-edit-trade-requirement-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { ToastService } from 'src/app/services/toast.service';
import { TradeService } from 'src/app/services/trade.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-admin-admin-trade-requirements',
  templateUrl: './admin-admin-trade-requirements.component.html',
  styleUrl: './admin-admin-trade-requirements.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdminAdminTradeRequirementsComponent {
  constructor(
    private ToastService: ToastService,
    private TradeService: TradeService,
    private ModalService: NgbModal,
    private UtilityService: UtilityService,
    private AuthService: AuthService
  ) {}

  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['edit', 'delete']
  };
  suppliedGridQuery: GenericObject = {
    attributes: ['PrimaryTradeId', 'SubNationalDivisionId'],
    SubNationalDivision: {},
    PrimaryTrade: {
      attributes: ['id']
    }
  };
  reloadGrid: boolean = true;

  LoggedInUser = this.AuthService.getUser();

  hasValidEditPermission() {
    if (this.LoggedInUser) {
      const { UserAccountRole: { role } = {} } = this.LoggedInUser;

      return role && new Set(['ADMIN_ADMIN', 'ADMIN_FRAUD_TEAM']).has(role);
    }

    return false;
  }

  action({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: PrimaryTradeLicenseRequirement;
  }) {
    const { id } = rowData;

    if (action === 'edit') {
      this.addEdit(rowData);
    }

    if (action === 'delete') {
      const { componentInstance, result } = this.ModalService.open(
        ConfirmModalComponent,
        { centered: true, size: 'md' }
      );

      Object.assign(componentInstance, {
        displayText: 'Are you sure you want to delete this trade requirement?'
      });

      from(result).subscribe({
        next: (confirm) => {
          if (confirm) {
            this.TradeService.deleteTradeRequirement(
              this.UtilityService.stringifyObjectsInQueryObject({
                id
              })
            ).subscribe({
              next: () => {
                this.ToastService.addSuccessToast(
                  'Success',
                  'Successfully deleted trade requirement.'
                );
                this.reloadGrid = true;
              }
            });
          }
        }
      });
    }
  }

  addEdit(
    existingPrimaryTradeLicenseRequirement?: PrimaryTradeLicenseRequirement
  ) {
    const { componentInstance, result } = this.ModalService.open(
      AddEditTradeRequirementModalComponent,
      { centered: true, size: 'lg' }
    );
    const tradeRequirement = existingPrimaryTradeLicenseRequirement
      ? existingPrimaryTradeLicenseRequirement
      : new PrimaryTradeLicenseRequirement({});

    Object.assign(componentInstance, {
      PrimaryTradeLicenseRequirement: tradeRequirement
    });

    from(result).subscribe({
      next: ({
        SelectedStates,
        SelectedTrades
      }: {
        SelectedStates: SubNationalDivision[];
        SelectedTrades: PrimaryTrade[];
      }) => {
        const TradesToSave: PrimaryTrade[] = [];
        SelectedTrades.forEach((Trade) => {
          const { id: PrimaryTradeId } = Trade;

          SelectedStates.forEach((SubNationalDivision) => {
            const clonedPrimaryTradeLicenseRequirement =
              structuredClone(tradeRequirement);
            const { id, abbreviation } = SubNationalDivision;

            if (id && abbreviation) {
              Object.assign(clonedPrimaryTradeLicenseRequirement, {
                SubNationalDivisionId: id,
                state: abbreviation,
                PrimaryTradeId
              });

              TradesToSave.push(clonedPrimaryTradeLicenseRequirement);
            }
          });
        });

        this.TradeService.saveTradeRequirements(TradesToSave).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success',
              'Successfully saved trade requirements.'
            );
            this.reloadGrid = true;
          }
        });
      }
    });
  }

  save(PrimaryTradeLicenseRequirement: PrimaryTradeLicenseRequirement) {
    if (!PrimaryTradeLicenseRequirement) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot save. No trade requirement found.',
        { returnError: true }
      );
    }

    this.TradeService.saveTradeRequirement(
      PrimaryTradeLicenseRequirement
    ).subscribe({
      next: () => {
        this.ToastService.addSuccessToast(
          'Success',
          'Successfully saved trade requirement.'
        );
        this.reloadGrid = true;
      }
    });
  }
}
